/**
 * P-1 会議一覧 (`/board-meetings`)。設計: docs/design/v05-board-meeting-pdca-detail.md §7-1。
 *
 * ⚠️ 対象組織フィルタ・カードのバッジ名は `GET /board-meetings/creatable-nodes`
 * (`board_meeting:create` スコープ) から解決する。`GET /nodes` は権限で絞られずテナント全ノードを
 * 返すため使わない (§8-182 と同型)。view はあるが create を持たない稀なロールでは、そのユーザーの
 * スコープ外ノードの会議カードで名前解決に失敗し ID 直書きへフォールバックしうるが、
 * データ漏洩ではなく表示上の劣化に留まる (実装時の簡略化判断)。
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { apiFetch, ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BoardEmptyState } from '@/components/board/BoardEmptyState';
import { MonthPicker } from '@/components/MonthPicker';
import { boardNodePathTo, type BoardNode } from '@/lib/board-node';

type BoardMeeting = {
  meeting_id: string;
  title: string;
  meeting_date: string;
  target_month: string | null;
  scope_node_id: string;
  attendees_note: string | null;
  status: 'planned' | 'closed';
  created_at: string;
  updated_at: string;
  row_version: number;
};
type BoardBriefSummary = { brief_id: string; scope_node_id: string; target_month: string; status: string };

const todayLocalDateString = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

type Draft = { title: string; meetingDate: string; targetMonth: string; scopeNodeId: string; attendeesNote: string };

export function BoardMeetingsListPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [nodeFilter, setNodeFilter] = useState<string>('__all__');
  const [draft, setDraft] = useState<Draft | null>(null);

  const { data: nodesData } = useQuery<{ nodes: BoardNode[] }>({
    queryKey: ['board-meetings', 'creatable-nodes'],
    queryFn: () => apiFetch('/board-meetings/creatable-nodes'),
  });
  const creatableNodes = nodesData?.nodes ?? [];
  const nodeById = useMemo(() => new Map(creatableNodes.map((n) => [n.node_id, n])), [creatableNodes]);

  const { data: meetingsData, isLoading, isError } = useQuery<{ meetings: BoardMeeting[] }>({
    queryKey: ['board-meetings'],
    queryFn: () => apiFetch('/board-meetings'),
  });
  const meetings = meetingsData?.meetings ?? [];

  const { data: briefsData } = useQuery<{ briefs: BoardBriefSummary[] }>({
    queryKey: ['board-briefs'],
    queryFn: () => apiFetch('/board-briefs'),
  });
  const briefKeys = useMemo(
    () => new Set((briefsData?.briefs ?? []).map((b) => `${b.scope_node_id}|${b.target_month}`)),
    [briefsData],
  );
  const hasBrief = (m: BoardMeeting) => !!m.target_month && briefKeys.has(`${m.scope_node_id}|${m.target_month}`);

  // ⚠️ §7-1: フィルタの選択肢は API が返した会議の node_id から構築する (GET /nodes は使わない)
  const filterNodeIds = useMemo(() => [...new Set(meetings.map((m) => m.scope_node_id))], [meetings]);

  const visibleMeetings = useMemo(() => {
    const filtered = nodeFilter === '__all__' ? meetings : meetings.filter((m) => m.scope_node_id === nodeFilter);
    return [...filtered].sort((a, b) => (a.meeting_date < b.meeting_date ? 1 : -1));
  }, [meetings, nodeFilter]);

  const createMut = useMutation({
    mutationFn: (d: Draft) => apiFetch<{ meeting_id: string }>('/board-meetings', {
      method: 'POST',
      body: JSON.stringify({
        title: d.title, meeting_date: d.meetingDate, target_month: d.targetMonth || null,
        scope_node_id: d.scopeNodeId, attendees_note: d.attendeesNote || null,
      }),
    }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['board-meetings'] });
      setDraft(null);
      toast.success('会議を作成しました');
      navigate(`/board-meetings/${res.meeting_id}`);
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '会議の作成に失敗しました'),
  });

  const openCreateModal = () => {
    // §7-1: 既定は自分のスコープの最上位ノード (creatable-nodes が depth_level ASC で返す先頭)。
    // 候補1件ならセレクタを出さず固定表示 (フォーム内で判定する)
    const defaultNodeId = creatableNodes[0]?.node_id ?? '';
    setDraft({ title: '', meetingDate: todayLocalDateString(), targetMonth: '', scopeNodeId: defaultNodeId, attendeesNote: '' });
  };

  const canCreate = creatableNodes.length > 0;
  const canSaveDraft = !!draft && draft.title.trim() !== '' && draft.meetingDate !== '' && draft.scopeNodeId !== '';

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-page-title text-text-main">会議</h1>
          <p className="text-sm text-text-sub mt-1">月次の経営会議とAI生成ブリーフを管理します。</p>
        </div>
        {canCreate && (
          <Button variant="primary" leftIcon={<Plus size={16} />} onClick={openCreateModal}>会議を作成</Button>
        )}
      </div>

      {filterNodeIds.length > 1 && (
        <div className="max-w-xs">
          <Select value={nodeFilter} onValueChange={setNodeFilter}>
            <SelectTrigger aria-label="対象組織で絞り込み" className="min-h-[44px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">すべての組織</SelectItem>
              {filterNodeIds.map((id) => (
                <SelectItem key={id} value={id}>{nodeById.get(id)?.name ?? id}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {isLoading ? (
        <p className="text-sm text-text-sub">読み込んでいます…</p>
      ) : isError ? (
        <p role="alert" className="text-sm text-alert-text">会議一覧の取得に失敗しました。</p>
      ) : visibleMeetings.length === 0 ? (
        <BoardEmptyState
          title="まだ会議がありません"
          action={canCreate ? { label: '会議を作成', onClick: openCreateModal } : undefined}
        />
      ) : (
        <ul className="space-y-2">
          {visibleMeetings.map((m) => {
            const node = nodeById.get(m.scope_node_id);
            const path = node ? boardNodePathTo(creatableNodes, m.scope_node_id) : [];
            const ancestorLabel = path.slice(0, -1).map((n) => n.name).join(' › ');
            return (
              <li key={m.meeting_id}>
                <button
                  type="button"
                  onClick={() => navigate(`/board-meetings/${m.meeting_id}`)}
                  className={`w-full text-left border border-surface-border rounded-lg p-3 min-h-[44px] transition hover:bg-surface-sunken ${
                    m.status === 'closed' ? 'bg-surface-sunken' : 'bg-white'
                  }`}
                >
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-text-body tabular-nums">{m.meeting_date}</span>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {hasBrief(m) && (
                        <span className="text-[11px] px-1.5 py-0.5 rounded bg-group-board-bg text-group-board whitespace-nowrap">ブリーフあり</span>
                      )}
                      <span className={`text-[11px] px-1.5 py-0.5 rounded border whitespace-nowrap ${
                        m.status === 'closed' ? 'bg-surface-card text-text-sub border-surface-border' : 'bg-success-bg text-success-text border-success-border'
                      }`}>
                        {m.status === 'closed' ? '終了' : '予定'}
                      </span>
                    </div>
                  </div>
                  <p className="text-sm text-text-body mt-1 line-clamp-2 break-words">{m.title}</p>
                  <div className="flex items-center gap-1.5 flex-wrap mt-1">
                    {node && (
                      <span className="text-[11px] px-1.5 py-0.5 rounded bg-surface-sunken text-text-sub whitespace-nowrap">{node.node_type_label}</span>
                    )}
                    <span className="text-xs text-text-sub">{m.target_month ? `${m.target_month.slice(0, 7)} 対象` : '臨時会議'}</span>
                  </div>
                  {ancestorLabel && (
                    <p className="text-[11px] text-text-sub truncate mt-0.5" title={ancestorLabel}>{ancestorLabel}</p>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <Modal
        open={!!draft} title="会議を作成" onClose={() => setDraft(null)} size="md"
        footer={draft && (
          <>
            <Button variant="secondary" onClick={() => setDraft(null)}>キャンセル</Button>
            <Button variant="primary" disabled={!canSaveDraft || createMut.isPending} loading={createMut.isPending}
              onClick={() => draft && createMut.mutate(draft)}>作成</Button>
          </>
        )}
      >
        {draft && (
          <div className="space-y-3">
            <div>
              <label className="block text-xs text-text-sub mb-1" htmlFor="bm-title">タイトル</label>
              <input id="bm-title" type="text" value={draft.title} maxLength={200}
                onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                className="w-full min-h-[44px] px-3 rounded border border-surface-border text-sm" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-text-sub mb-1" htmlFor="bm-date">開催日</label>
                <input id="bm-date" type="date" value={draft.meetingDate}
                  onChange={(e) => setDraft({ ...draft, meetingDate: e.target.value })}
                  className="w-full min-h-[44px] px-3 rounded border border-surface-border text-sm" />
              </div>
              <div>
                <label className="block text-xs text-text-sub mb-1" htmlFor="bm-month">対象月 (任意)</label>
                {/* ネイティブ<input type="month">はSafariで非対応のため生テキスト入力に
                    フォールバックし書式検証が効かない(2026-09-15実機で発覚)。MonthPickerに置き換える */}
                <MonthPicker id="bm-month" aria-label="対象月" value={draft.targetMonth ?? ''}
                  onChange={(v) => setDraft({ ...draft, targetMonth: v ? `${v}-01` : '' })}
                  className="w-full min-h-[44px] px-3 rounded border border-surface-border text-sm" />
              </div>
            </div>
            <p className="text-xs text-text-sub">
              対象月 = この会議で振り返りたい実績の月（例: 8月の実績を話し合うなら「8月」）。
              設定するとブリーフを生成できます。
            </p>
            <div>
              <label className="block text-xs text-text-sub mb-1" htmlFor="bm-node">対象組織</label>
              {creatableNodes.length <= 1 ? (
                <p className="text-sm text-text-body min-h-[44px] flex items-center px-3 rounded border border-surface-border bg-surface-sunken">
                  {creatableNodes[0]?.name ?? '(対象組織なし)'}
                </p>
              ) : (
                <Select value={draft.scopeNodeId} onValueChange={(v) => setDraft({ ...draft, scopeNodeId: v })}>
                  <SelectTrigger id="bm-node" aria-label="対象組織"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {creatableNodes.map((n) => (
                      <SelectItem key={n.node_id} value={n.node_id}>{'　'.repeat(n.depth_level)}{n.name} ({n.node_type_label})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div>
              <label className="block text-xs text-text-sub mb-1" htmlFor="bm-note">出席メモ (任意)</label>
              <textarea id="bm-note" value={draft.attendeesNote} maxLength={2000} rows={3}
                onChange={(e) => setDraft({ ...draft, attendeesNote: e.target.value })}
                className="w-full px-3 py-2 rounded border border-surface-border text-sm" />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
